import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AñoEscolar } from '../types/usuario.types';
import { CursosEstudianteResponse, CursoEstudianteItem, MatriculaResponse } from '../types/matricula.types';
import { PeriodoResponse } from '../types/periodo.types';
import { NotaActividadResponse } from '../types/notaActividad.types';
import { catalogoService } from '../services/usuarioService';
import { matriculaService } from '../services/matriculaService';
import { periodoService } from '../services/periodoService';
import { notaActividadService } from '../services/notaActividadService';
import { httpClient } from '../utils/httpClient';

// ============================================================
// useMisNotas – flujo del estudiante para ver sus notas
//   1. Selecciona año escolar → carga cursos + matrícula
//   2. Selecciona curso → carga periodos
//   3. Selecciona periodo → carga notas
// ============================================================

export const useMisNotas = () => {
  const { user } = useAuth();

  // ── Catálogos ─────────────────────────────────────────────
  const [añosEscolares, setAñosEscolares]       = useState<AñoEscolar[]>([]);
  const [anioSeleccionado, setAnioSeleccionado]  = useState<number | null>(null);

  // ── Cursos del estudiante ─────────────────────────────────
  const [cursosData, setCursosData]       = useState<CursosEstudianteResponse | null>(null);
  const [loadingCursos, setLoadingCursos] = useState(true);
  const [error, setError]                 = useState<string | null>(null);

  // ── Matrícula del estudiante para el año seleccionado ─────
  const [matriculaId, setMatriculaId] = useState<number | null>(null);

  // ── Curso seleccionado → periodos ─────────────────────────
  const [selectedCurso, setSelectedCurso]           = useState<CursoEstudianteItem | null>(null);
  const [periodos, setPeriodos]                     = useState<PeriodoResponse[]>([]);
  const [selectedPeriodoId, setSelectedPeriodoId]   = useState<number | null>(null);
  const [loadingPeriodos, setLoadingPeriodos]       = useState(false);

  // ── Notas ─────────────────────────────────────────────────
  const [notas, setNotas]               = useState<NotaActividadResponse[]>([]);
  const [loadingNotas, setLoadingNotas] = useState(false);

  // ── 1. Cargar años escolares ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const anios = await catalogoService.getAñosEscolares();
        if (cancelled) return;
        setAñosEscolares(anios);
        if (anios.length > 0) setAnioSeleccionado(anios[0].idAnioEscolar);
      } catch {
        if (!cancelled) { setError('No se pudieron cargar los años escolares'); setLoadingCursos(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── 2. Cargar cursos + matrícula cuando cambia el año ─────
  useEffect(() => {
    if (!user || !anioSeleccionado) return;
    let cancelled = false;

    (async () => {
      setLoadingCursos(true); setError(null);
      setSelectedCurso(null); setPeriodos([]); setSelectedPeriodoId(null); setNotas([]);
      try {
        const [cursosResp, matriculas] = await Promise.all([
          matriculaService.getCursosByEstudiante(user.idUsuario, anioSeleccionado),
          httpClient.get<MatriculaResponse[]>(`/matriculas/estudiante/${user.idUsuario}`),
        ]);
        if (cancelled) return;
        setCursosData(cursosResp);
        const mat = Array.isArray(matriculas)
          ? matriculas.find(m => m.anioEscolarId === anioSeleccionado && m.activo)
          : null;
        setMatriculaId(mat ? mat.idMatricula : null);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error al cargar cursos');
          setCursosData(null);
          setMatriculaId(null);
        }
      } finally {
        if (!cancelled) setLoadingCursos(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, anioSeleccionado]);

  // ── 3. Seleccionar curso → cargar periodos ────────────────
  const selectCurso = useCallback((curso: CursoEstudianteItem) => {
    setSelectedCurso(curso);
    setSelectedPeriodoId(null);
    setNotas([]);

    if (!anioSeleccionado) return;
    setLoadingPeriodos(true);
    periodoService.listarPorAnioEscolar(anioSeleccionado)
      .then(setPeriodos)
      .catch(() => setPeriodos([]))
      .finally(() => setLoadingPeriodos(false));
  }, [anioSeleccionado]);

  // ── 4. Seleccionar periodo → cargar notas ─────────────────
  const selectPeriodo = useCallback((periodoId: number) => {
    setSelectedPeriodoId(periodoId);
    setLoadingNotas(true);
    setNotas([]);

    if (!selectedCurso || !matriculaId) {
      setLoadingNotas(false);
      return;
    }

    notaActividadService.listarPorMatriculaAsignacionPeriodo(
      matriculaId,
      selectedCurso.asignacionAcademicaId,
      periodoId,
    )
      .then(setNotas)
      .catch(() => setNotas([]))
      .finally(() => setLoadingNotas(false));
  }, [selectedCurso, matriculaId]);

  // ── Volver al listado de cursos ───────────────────────────
  const volverACursos = useCallback(() => {
    setSelectedCurso(null);
    setPeriodos([]);
    setSelectedPeriodoId(null);
    setNotas([]);
  }, []);

  const cambiarAnio = useCallback((id: number) => {
    setAnioSeleccionado(id);
  }, []);

  return {
    añosEscolares, anioSeleccionado,
    cursosData, loadingCursos, error,
    selectedCurso, periodos, selectedPeriodoId, loadingPeriodos,
    notas, loadingNotas, matriculaId,
    cambiarAnio, selectCurso, selectPeriodo, volverACursos,
  };
};
